import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DataForSEOService } from '../../services/dataforseo';

interface ContentElement {
  type: 'heading' | 'text' | 'link' | 'table';
  level?: number; // for headings (h1, h2, etc.)
  content: string;
  href?: string; // for links
  tableData?: any[]; // for table content
}

@Component({
  selector: 'app-url-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './url-detail.html',
  styleUrl: './url-detail.css'
})
export class UrlDetailComponent implements OnInit {
  url: string | null = null;
  dataForSEOStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  dataForSEOMessage: string = '';
  analysisData: any = null;
  analysisStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  issuesSectionCollapsed: boolean = false;
  contentSectionCollapsed: boolean = true;
  contentElements: ContentElement[] = [];

  constructor(
    private route: ActivatedRoute,
    private dataForSEOService: DataForSEOService
  ) {}

  ngOnInit() {
    // Decode the URL from the route parameter
    this.url = decodeURIComponent(this.route.snapshot.paramMap.get('url') || '');
    
    // Load analysis data if we have a URL
    if (this.url) {
      this.loadAnalysisData();
    }
  }

  loadAnalysisData(): void {
    if (!this.url) return;
    
    this.analysisStatus = 'loading';
    this.dataForSEOService.getUrlAnalysis(this.url).subscribe({
      next: (response) => {
        if (response.success) {
          this.analysisData = response.data.analysis;
          this.analysisStatus = 'success';
          this.extractContentElements();
        } else {
          this.analysisStatus = 'error';
        }
      },
      error: (error) => {
        console.error('Failed to load analysis:', error);
        this.analysisStatus = 'error';
      }
    });
  }

  /**
   * Extract key content elements from structured content
   */
  extractContentElements(): void {
    const structuredContent = this.getStructuredContent();
    if (!structuredContent) return;

    this.contentElements = [];
    
    // Process main_topic content
    if (structuredContent.main_topic && Array.isArray(structuredContent.main_topic)) {
      structuredContent.main_topic.forEach((topic: any) => {
        // Add the main heading
        if (topic.h_title) {
          this.contentElements.push({
            type: 'heading',
            level: topic.level || 1,
            content: topic.h_title
          });
        }
        
        // Add primary content
        if (topic.primary_content && Array.isArray(topic.primary_content)) {
          topic.primary_content.forEach((content: any) => {
            if (content.text && content.text.trim()) {
              this.contentElements.push({
                type: 'text',
                content: content.text.trim()
              });
            }
            
            // Add links from the content
            if (content.urls && Array.isArray(content.urls)) {
              content.urls.forEach((url: any) => {
                if (url.anchor_text && url.url) {
                  this.contentElements.push({
                    type: 'link',
                    content: url.anchor_text,
                    href: url.url
                  });
                }
              });
            }
          });
        }
        

      });
    }
    
    // Process header content
    if (structuredContent.header) {
      // Add primary header content
      if (structuredContent.header.primary_content && Array.isArray(structuredContent.header.primary_content)) {
        structuredContent.header.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'heading',
              level: 1,
              content: content.text.trim()
            });
          }
          
          // Add links from header
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
      

    }
    
    // Process footer content
    if (structuredContent.footer) {
      // Add primary footer content only
      if (structuredContent.footer.primary_content && Array.isArray(structuredContent.footer.primary_content)) {
        structuredContent.footer.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'text',
              content: content.text.trim()
            });
          }
          
          // Add links from footer
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
    }
    
    // Process table content
    if (structuredContent.table_content && Array.isArray(structuredContent.table_content)) {
      structuredContent.table_content.forEach((table: any) => {
        if (table.body && Array.isArray(table.body)) {
          table.body.forEach((row: any, rowIndex: number) => {
            if (row.row_cells && Array.isArray(row.row_cells)) {
              // Create a table element with the row data
              this.contentElements.push({
                type: 'table',
                content: `Table Row ${rowIndex + 1}`,
                tableData: row.row_cells.map((cell: any) => ({
                  text: cell.text || '',
                  isHeader: cell.is_header || false,
                  urls: cell.urls || null
                }))
              });
            }
          });
        }
      });
    }
  }

  /**
   * Test DataForSEO connection for this URL
   * TODO: This is where we'll gather data for each URL for now
   * TODO: The button will not stay, it will be automatically retrieved every X days
   */
  fetchDataForSEO(): void {
    this.dataForSEOStatus = 'loading';
    this.dataForSEOMessage = 'Testing DataForSEO connection...';

    this.dataForSEOService.testConnection().subscribe({
      next: (response) => {
        if (response.success && response.data.healthy) {
          this.dataForSEOStatus = 'success';
          this.dataForSEOMessage = 'DataForSEO connection works! ✅';
        } else {
          this.dataForSEOStatus = 'error';
          this.dataForSEOMessage = 'DataForSEO connection failed! ❌';
        }
      },
      error: (error) => {
        this.dataForSEOStatus = 'error';
        this.dataForSEOMessage = 'DataForSEO connection failed! ❌';
        console.error('DataForSEO test failed:', error);
      }
    });
  }

  getScoreClass(score: number): string {
    if (score >= 90) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  }

  toggleIssuesSection(): void {
    this.issuesSectionCollapsed = !this.issuesSectionCollapsed;
  }

  toggleContentSection(): void {
    this.contentSectionCollapsed = !this.contentSectionCollapsed;
  }

  /**
   * Get the structured content from the analysis data
   */
  getStructuredContent(): any {
    if (!this.analysisData?.rawData?.structuredContent) {
      return null;
    }
    return this.analysisData.rawData.structuredContent;
  }

  // TODO: DataForSEO supports markdown_view: true parameter to get page_as_markdown
  // This would provide clean markdown content instead of structured content
  // Consider implementing this in the future for better content display

  /**
   * Get heading level class for styling
   */
  getHeadingClass(level: number): string {
    return `heading-level-${level}`;
  }

  /**
   * Check if content has any elements
   */
  hasContent(): boolean {
    return this.contentElements.length > 0;
  }

  /**
   * TrackBy function for ngFor performance
   */
  trackByElement(index: number, element: ContentElement): string {
    return `${element.type}-${index}-${element.content.substring(0, 20)}`;
  }
} 